import {
  computed,
  inject,
  onBeforeUnmount,
  provide,
  ref,
  shallowRef,
  unref,
  watch,
  type Ref,
  type InjectionKey,
} from 'vue';
import { useApolloClient } from '@vue/apollo-composable';
import {
  ProjectTaskChangesDocument,
  ProjectTaskDetailsDocument,
  type ComputeTaskType,
} from '@/gql/graphql';

const ACTIVE = new Set(['PENDING', 'CREATING', 'RUNNING', 'ABORTING']);
const FULL_REFRESH_MS = 10 * 60 * 1000;
type Polling = ReturnType<typeof createProjectTaskPolling>;
const projectPollingKey: InjectionKey<Polling> = Symbol('project-task-polling');

// PostgreSQL markers carry microseconds; Date.parse alone would collapse
// distinct updates within the same millisecond.
function markerTime(value: string) {
  const fraction = value.match(/\.(\d+)/)?.[1] || '';
  return Date.parse(value) * 1000 + Number(fraction.padEnd(6, '0').slice(3, 6));
}

function createProjectTaskPolling(
  projectId: Ref<number>,
  refreshMetadata?: () => void
) {
  const { resolveClient } = useApolloClient();
  const tasks = shallowRef<ComputeTaskType[]>([]);
  const loading = ref(false);
  const error = shallowRef<Error | null>(null);
  const detailErrors = ref<Record<number, string>>({});
  const expanded = new Set<number>();
  const details = new Map<number, string>();
  const pending = new Map<
    number,
    {
      marker: string;
      promise: Promise<ComputeTaskType | undefined>;
      cancel: () => void;
    }
  >();
  const visible = new Map<symbol, number[]>();
  let cursor: string | null = null;
  let fullAt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let inFlight = false;
  let control: AbortController | undefined;
  let fullPending = false;
  let metadataPending = false;
  let generation = 0;
  let stopped = false;

  async function detail(id: number): Promise<ComputeTaskType | undefined> {
    const summary = tasks.value.find((task) => task.id === id);
    if (
      !summary ||
      !expanded.has(id) ||
      document.hidden ||
      ![...visible.values()].some((ids) => ids.includes(id))
    )
      return;
    const marker = summary.changedAt;
    if (details.get(id) === marker) return summary;
    const existing = pending.get(id);
    if (existing) return existing.promise;
    const version = generation;
    const controller = new AbortController();
    let cancel!: () => void;
    let deadline: ReturnType<typeof setTimeout> | undefined;
    const expiration = new Promise<never>((_resolve, reject) => {
      cancel = () => {
        clearTimeout(deadline);
        controller.abort();
        reject(new Error('Task detail request expired'));
      };
      deadline = setTimeout(cancel, 30000);
    });
    let catchUp = false;
    const promise = (async () => {
      try {
        const response = await Promise.race([
          resolveClient().query({
            query: ProjectTaskDetailsDocument,
            variables: { id, projectId: projectId.value },
            fetchPolicy: 'no-cache',
            context: {
              fetchOptions: { signal: controller.signal },
              queryDeduplication: false,
            },
          }),
          expiration,
        ]);
        if (version !== generation || !expanded.has(id) || document.hidden)
          return;
        const task = response.data.computeTask as ComputeTaskType | null;
        const latest = tasks.value.find((item) => item.id === id);
        if (!latest) return;
        if (!task || !Number.isFinite(Date.parse(task.changedAt)))
          throw new Error('Task details unavailable');
        if (markerTime(task.changedAt) < markerTime(latest.changedAt)) {
          catchUp = latest.changedAt !== marker;
          return;
        }
        details.set(id, task.changedAt);
        tasks.value = tasks.value.map((item) =>
          item.id === id
            ? {
                ...item,
                ...task,
                results: {
                  ...task.results,
                  filesAvailable: item.results?.filesAvailable,
                },
              }
            : item
        );
        delete detailErrors.value[id];
        return tasks.value.find((item) => item.id === id);
      } catch {
        if (
          version === generation &&
          !document.hidden &&
          pending.get(id)?.marker === marker
        )
          detailErrors.value[id] =
            'Latest task results could not be loaded. Retrying automatically.' +
            (details.has(id)
              ? ' Previously loaded results remain visible.'
              : ' Open-card results are not available yet.');
      } finally {
        clearTimeout(deadline);
        if (version === generation && pending.get(id)?.marker === marker) {
          pending.delete(id);
          if (catchUp) void detail(id);
        }
      }
    })();
    pending.set(id, { marker, promise, cancel });
    return promise;
  }

  function schedule() {
    clearTimeout(timer);
    if (stopped || document.hidden) return;
    const ids = new Set([...visible.values()].flat());
    const active = tasks.value.some(
      (task) => ids.has(task.id!) && ACTIVE.has(task.status || '')
    );
    timer = setTimeout(() => void refresh(), active ? 3000 : 30000);
  }

  async function refresh(full = false, metadata = false) {
    if (stopped || document.hidden) return;
    if (inFlight) {
      fullPending ||= full;
      metadataPending ||= metadata;
      return;
    }
    inFlight = true;
    const controller = new AbortController();
    control = controller;
    clearTimeout(timer);
    const version = generation;
    const periodic = !!cursor && Date.now() - fullAt >= FULL_REFRESH_MS;
    const replace = full || !cursor || periodic;
    loading.value = cursor === null;
    let expire!: () => void;
    const expiration = new Promise<never>((_resolve, reject) => {
      // Settle even if a transport ignores abort, so it cannot hold inFlight forever.
      expire = () => reject(new Error('Project task request expired'));
      controller.signal.addEventListener('abort', expire, { once: true });
    });
    const deadline = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await Promise.race([
        resolveClient().query({
          query: ProjectTaskChangesDocument,
          variables: {
            projectId: projectId.value,
            since: replace ? null : cursor,
          },
          fetchPolicy: 'no-cache',
          context: {
            fetchOptions: { signal: controller.signal },
            queryDeduplication: false,
          },
        }),
        expiration,
      ]);
      if (version !== generation) return;
      const changes = response.data.projectTaskChanges;
      const next = new Map(
        (replace ? [] : tasks.value).map((task) => [task.id, task])
      );
      for (const summary of changes?.tasks || []) {
        const previous = tasks.value.find((task) => task.id === summary.id);
        if (
          previous &&
          markerTime(previous.changedAt) >= markerTime(summary.changedAt)
        ) {
          next.set(summary.id, previous);
          continue;
        }
        next.set(summary.id, {
          ...previous,
          ...summary,
          attributes: previous?.attributes || {},
          results: {
            ...previous?.results,
            filesAvailable: summary.hasFiles,
            reason: summary.errorReason,
            progressTotal: summary.progressTotal,
            progressDescription: summary.progressDescription,
            message: summary.errorMessage,
          },
        } as ComputeTaskType);
      }
      const ordered = [...next.values()].sort(
        (a, b) =>
          String(b.createdAt).localeCompare(String(a.createdAt)) ||
          (b.id || 0) - (a.id || 0)
      );
      if (
        ordered.length !== tasks.value.length ||
        ordered.some((task, index) => task !== tasks.value[index])
      ) {
        tasks.value = ordered;
      }
      cursor = changes!.checkedAt;
      if (replace) {
        fullAt = Date.now();
        if (metadata || periodic) refreshMetadata?.();
        for (const id of details.keys()) if (!next.has(id)) details.delete(id);
      }
      error.value = null;
      for (const id of expanded) void detail(id);
    } catch {
      if (version === generation && !document.hidden)
        error.value = new Error(
          'Project task updates could not be loaded. Retrying automatically.'
        );
    } finally {
      clearTimeout(deadline);
      controller.signal.removeEventListener('abort', expire);
      if (version === generation) {
        loading.value = false;
        inFlight = false;
        if (fullPending) {
          fullPending = false;
          const notify = metadataPending;
          metadataPending = false;
          void refresh(true, notify);
        } else schedule();
      }
    }
  }

  function visibility() {
    clearTimeout(timer);
    if (document.hidden) control?.abort();
    else void refresh(true, true);
  }
  document.addEventListener('visibilitychange', visibility);
  watch(
    projectId,
    () => {
      generation++;
      inFlight = false;
      control?.abort();
      fullPending = false;
      metadataPending = false;
      clearTimeout(timer);
      cursor = null;
      tasks.value = [];
      details.clear();
      expanded.clear();
      for (const request of pending.values()) request.cancel();
      pending.clear();
      detailErrors.value = {};
      void refresh(true);
    },
    { immediate: true }
  );
  onBeforeUnmount(() => {
    stopped = true;
    generation++;
    control?.abort();
    for (const request of pending.values()) request.cancel();
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', visibility);
  });
  return {
    tasks,
    loading,
    error,
    detailErrors,
    refresh,
    async setExpanded(id: number, open: boolean) {
      if (open) {
        expanded.add(id);
        return await detail(id);
      } else expanded.delete(id);
    },
    setVisible(key: symbol, ids: number[]) {
      const previous = visible.get(key);
      visible.set(key, ids);
      if (
        !previous ||
        previous.length !== ids.length ||
        ids.some((id, index) => id !== previous[index])
      )
        schedule();
    },
    removeVisible(key: symbol) {
      visible.delete(key);
    },
  };
}

export function provideProjectTaskPolling(
  projectId: Ref<number>,
  refreshMetadata?: () => void
) {
  const polling = createProjectTaskPolling(projectId, refreshMetadata);
  provide(projectPollingKey, polling);
  return polling;
}

// Only filters available from the lightweight project summaries are exposed.
type ProjectTaskFilters = {
  projectId: number | null;
  taskTypes?: string[] | null;
  excludeSubtaskTypes?: string[] | null;
  pagingItemsPerPage?: number;
  pagingPageCurrent?: number;
};

/** Project lists have one required stream owner in ProjectDetail. */
export function useProjectTaskQuery(
  variables: ProjectTaskFilters | Ref<ProjectTaskFilters>
) {
  const polling = inject(projectPollingKey);
  if (!polling)
    throw new Error('Project task lists require a polling provider');
  const key = Symbol('visible-list');
  const result = computed(() => {
    const filters = unref(variables);
    const all = polling.tasks.value;
    const rows = all
      .filter(
        (task) =>
          task.projectId === filters.projectId &&
          (!filters.taskTypes?.length ||
            filters.taskTypes.includes(task.taskType || '')) &&
          (!filters.excludeSubtaskTypes?.length ||
            !all.some(
              (child) =>
                child.parentId === task.id &&
                filters.excludeSubtaskTypes?.includes(child.taskType || '')
            ))
      )
      .map((task) => ({
        ...task,
        subtasks: all
          .filter(
            (child) =>
              child.parentId === task.id &&
              (!filters.taskTypes?.length ||
                filters.taskTypes.includes(child.taskType || ''))
          )
          .sort(
            (a, b) =>
              String(a.createdAt).localeCompare(String(b.createdAt)) ||
              (a.id || 0) - (b.id || 0)
          ),
      }));
    const size = filters.pagingItemsPerPage || 0;
    const page = filters.pagingPageCurrent || 1;
    const selected =
      size > 0 ? rows.slice((page - 1) * size, page * size) : rows;
    return { computeTasks: { total: rows.length, computeTasks: selected } };
  });
  onBeforeUnmount(() => polling.removeVisible(key));
  return {
    result,
    loading: polling.loading,
    error: polling.error,
    refetch: () => polling.refresh(true),
    onResult: (
      callback: (value: {
        data: {
          computeTasks: { total: number; computeTasks: ComputeTaskType[] };
        };
        loading: boolean;
      }) => void
    ) =>
      watch(
        [result, polling.loading],
        () => callback({ data: result.value, loading: polling.loading.value }),
        { immediate: true }
      ),
    onError: (callback: (error: Error) => void) =>
      watch(polling.error, (error) => {
        if (error) callback(error);
      }),
    setExpanded: polling.setExpanded,
    setVisibleTasks: (ids: number[]) => polling.setVisible(key, ids),
    detailErrors: polling.detailErrors,
  };
}
